import type { ConsoleBootstrap } from '@digger/shared';

/**
 * Operator authentication for the console.
 *
 * Relay mode: password → POST /api/login → short-lived JWT. The password
 * lives in module memory only (never storage) so reconnects can re-mint
 * silently; the JWT is what travels in the signaling hello and what the
 * agent itself verifies.
 *
 * Agent (LAN) mode with auth.required: the entered secret IS the token
 * (static shared token in digger.config.json) — no minting round trip.
 */
export interface TokenProvider {
  /** Resolve a currently-valid token; may show the login overlay and wait. */
  getToken(): Promise<string>;
  /**
   * The agent rejected our token. Returns true when fresh credentials can be
   * obtained (drop them and let the next connect re-prompt); false when this
   * is terminal — a preset booked-slot token has no fallback, so the caller
   * must stop retrying rather than re-send the same rejected token forever.
   */
  invalidate(): boolean;
}

const REMINT_MARGIN_MS = 30_000;

/** A token minted elsewhere (booked drive session) instead of password login. */
export interface PresetToken {
  token: string;
  /** The agent rejected the preset token — there is no password to fall back to. */
  onInvalid: () => void;
}

export function createTokenProvider(
  bootstrap: ConsoleBootstrap,
  preset?: PresetToken,
): TokenProvider | undefined {
  if (preset) {
    return {
      getToken: () => Promise.resolve(preset.token),
      invalidate: () => {
        preset.onInvalid();
        return false; // no password fallback — terminal, stop retrying
      },
    };
  }
  if (bootstrap.mode === 'agent' && !bootstrap.authRequired) return undefined;

  let password: string | undefined; // in-memory only
  let cached: { token: string; expiresAtMs: number } | undefined;

  const promptAndMint = async (initialError?: string): Promise<string> => {
    let error = initialError;
    for (;;) {
      password = await showOverlay(error);
      if (bootstrap.mode === 'agent') {
        // Static token path: the agent validates it on the hello. If it is
        // wrong we find out via invalidate() and re-prompt then.
        return password;
      }
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }).catch(() => undefined);
      if (res?.ok) {
        cached = (await res.json()) as { token: string; expiresAtMs: number };
        return cached.token;
      }
      password = undefined;
      error =
        res === undefined
          ? 'relay unreachable — try again'
          : ((await res.json().catch(() => undefined)) as { error?: string } | undefined)?.error ??
            `login failed (${res.status})`;
    }
  };

  return {
    async getToken() {
      if (bootstrap.mode === 'agent') {
        return password ?? promptAndMint();
      }
      if (cached && Date.now() < cached.expiresAtMs - REMINT_MARGIN_MS) return cached.token;
      if (password) {
        // Silent re-mint with the remembered password.
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        }).catch(() => undefined);
        if (res?.ok) {
          cached = (await res.json()) as { token: string; expiresAtMs: number };
          return cached.token;
        }
        if (res && (res.status === 401 || res.status === 403)) password = undefined;
      }
      return promptAndMint(password ? undefined : cached ? 'session expired — log in again' : undefined);
    },
    invalidate() {
      password = undefined;
      cached = undefined;
      return true; // next getToken() re-prompts, so retrying is worthwhile
    },
  };
}

/** Builds (once) and shows the overlay; resolves with the submitted secret. */
function showOverlay(error?: string): Promise<string> {
  let overlay = document.getElementById('login-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'login-overlay';
    overlay.innerHTML = `
      <form id="login-form">
        <h2>DIGGER OPERATOR</h2>
        <input id="login-password" type="password" placeholder="operator password"
               autocomplete="current-password" autofocus />
        <button type="submit">connect</button>
        <div id="login-error" hidden></div>
      </form>`;
    document.body.appendChild(overlay);
  }
  overlay.hidden = false;

  const form = overlay.querySelector('#login-form') as HTMLFormElement;
  const input = overlay.querySelector('#login-password') as HTMLInputElement;
  const errorEl = overlay.querySelector('#login-error') as HTMLElement;
  errorEl.hidden = !error;
  errorEl.textContent = error ?? '';
  input.value = '';
  input.focus();

  return new Promise((resolve) => {
    form.onsubmit = (e) => {
      e.preventDefault();
      const value = input.value;
      if (!value) return;
      form.onsubmit = null;
      overlay.hidden = true;
      // The password field must never keep focus: Space is E-STOP.
      input.blur();
      resolve(value);
    };
  });
}
